<script setup lang="ts">
import { Participant, Participants, Project } from "../../../types/PersologOnlineTypes";
import { ref, watch } from "vue";
import { ProfileAdministrationAPI, ProfileContractorAPI } from "../../../api";
import { useToast } from "primevue/usetoast";
import { useI18n } from "vue-i18n";
import { getDisplayName } from "../../../utils/GeneralUtils";
import { i18n } from "../../../main";
import { getChartPdfUrl } from "../../../utils/PdfUtils.ts";
import { BENCHMARK_BASIC, BENCHMARK_JOBMATCH } from "../../../utils/ProjectUtils";

const { t } = useI18n();

const props = defineProps({
    project: {
        type: Object as () => Project,
        required: true,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    hasGeneratedParticipant: {
        type: Boolean,
        required: true,
    },
    participants: {
        type: Object as () => Participant[],
        required: true,
    },
});

const emit = defineEmits(["profile:generated", "profile:generation:started", "profile:generation:cancelled"]);

const loading = ref(false);
const isDisabled = ref(props.disabled);

let pollingInterval: NodeJS.Timeout | undefined;

/**
 * Generates a benchmark profile for a in123 project.
 * No participants available -> generate IN120 profile
 * Participants available and generated -> generate IN122 profile
 */
async function generateRecruitingBenchmarkProfile(profileType: string = BENCHMARK_BASIC) {
    try {
        emit("profile:generation:started");
        loading.value = true;
        isDisabled.value = true;

        await deleteBenchmarkProfile();

        if (![BENCHMARK_BASIC, BENCHMARK_JOBMATCH].includes(profileType)) {
            throw new Error("Invalid profile type");
        }

        if (profileType === BENCHMARK_JOBMATCH) {
            await ProfileContractorAPI.generateGroupChart(
                profileType,
                props.project.id as number,
                {
                    participants: props.participants,
                } as Participants,
                props.project.language,
            );
        } else {
            await ProfileContractorAPI.generateBenchmarkProfile(
                profileType,
                props.project?.id as number,
                props.project?.language,
            );
        }
        showTriggeredToast(profileType);

        // Start polling until the profile is available
        pollingInterval = setInterval(async () => {
            let url: string | undefined;
            try {
                url = await getChartPdfUrl(profileType, props.project?.id as number, props.project?.language);
            } catch (error) {
                console.info(
                    "PDF in " +
                        getDisplayName(i18n.global.locale.value, props.project?.language) +
                        " still not available. Polling again in 10 seconds.",
                    error,
                );
            }
            if (url) {
                // Stop polling
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                }
                toast.add({
                    severity: "success",
                    summary: t("project-configuration.jobMatch.generation.toasts.successHeading"),
                    detail:
                        profileType === BENCHMARK_JOBMATCH
                            ? t("project-configuration.jobMatch.generation.toasts.successIN122")
                            : t("project-configuration.jobMatch.generation.toasts.successIN120"),
                    life: 8000,
                });
                emit("profile:generated");
                loading.value = false;
                isDisabled.value = false;
            }
        }, 10000); // Poll every 10 seconds

        // Stop polling after 2 minutes
        setTimeout(() => {
            if (pollingInterval) {
                clearInterval(pollingInterval);
                console.info("Timeout reached. Polling stopped after 4 minutes.");
                loading.value = false;
                isDisabled.value = false;
            }
        }, 120000); // 2 minutes
    } catch (error) {
        console.log("Error generating benchmark profile:", error);
        addToastError();
    }
}

const toast = useToast();

const addToastError = () => {
    toast.add({
        severity: "error",
        summary: t("project-configuration.jobMatch.generation.toasts.errorHeading"),
        detail: t("project-configuration.jobMatch.generation.toasts.error"),
        life: 8000,
    });
};

const showTriggeredToast = (profileType: string) => {
    toast.add({
        severity: "success",
        summary: t("project-configuration.jobMatch.generation.toasts.triggeredHeading"),
        detail:
            profileType === BENCHMARK_JOBMATCH
                ? t("project-configuration.jobMatch.generation.toasts.triggeredIN122")
                : t("project-configuration.jobMatch.generation.toasts.triggeredIN120"),
        life: 8000,
    });
};

function cancelGenerate() {
    if (pollingInterval) {
        clearInterval(pollingInterval);
        loading.value = false;
        isDisabled.value = false;
        emit("profile:generation:cancelled");
        toast.add({
            severity: "info",
            summary: t("project-configuration.jobMatch.generation.toasts.cancelledHeading"),
            detail: t("project-configuration.jobMatch.generation.toasts.cancelled"),
            life: 8000,
        });
    }
}

/**
 * Deletes any existing benchmark profile for the project.
 */
async function deleteBenchmarkProfile() {
    if (!props.project.id) return;
    try {
        await ProfileContractorAPI.deleteGroupChart(props.project.id, BENCHMARK_BASIC);
        await ProfileContractorAPI.deleteGroupChart(props.project.id, BENCHMARK_JOBMATCH);
        await ProfileAdministrationAPI.updateProject({ ...props.project, status: "CREATED" });
    } catch (error) {
        console.error("Error deleting group chart:", error);
    }
}

const menu = ref();
const items = ref([
    {
        items: [
            {
                label: t("project-configuration.jobMatch.generation.labelIN120"),
                command: () => {
                    generateRecruitingBenchmarkProfile(BENCHMARK_BASIC);
                },
                icon: "pi pi-gauge",
            },
            {
                label: t("project-configuration.jobMatch.generation.labelIN122"),
                command: () => {
                    generateRecruitingBenchmarkProfile(BENCHMARK_JOBMATCH);
                },
                icon: "pi pi-trophy",
            },
        ],
    },
]);

const toggle = (event: Event) => {
    menu.value.toggle(event);
};

watch(
    () => props.disabled,
    (value) => {
        isDisabled.value = value;
    },
);
</script>

<template>
    <Toast />
    <Button v-if="loading" icon="pi pi-times" @click="cancelGenerate" style="margin-left: 10px" :aria-label="t('accessibility.buttons.cancel')"></Button>

    <Button
        v-if="!hasGeneratedParticipant"
        :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-file'"
        :label="t('project-configuration.jobMatch.generation.label')"
        @click="generateRecruitingBenchmarkProfile(BENCHMARK_BASIC)"
        :disabled="isDisabled"
    ></Button>

    <Button
        v-else
        :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-file'"
        :label="t('project-details.participants.actionsLabels.generate-profile')"
        @click="toggle"
        :disabled="isDisabled"
        aria-controls="overlay_menu"
        aria-haspopup="true"
    ></Button>
    <Menu ref="menu" id="overlay_menu" :model="items" :popup="true" />
</template>
